import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Inject, NgZone, OnDestroy, PLATFORM_ID, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageProgressService } from '../../services/page-progress.service';

/// The thin progress line under the top bar: it starts on a navigation, creeps forward while the page and its first
/// requests load (never reaching the end), then runs to the end and fades.
@Component({
    selector: 'app-loading-bar',
    templateUrl: './loading-bar.component.html',
    styleUrls: ['./loading-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoadingBarComponent implements AfterViewInit, OnDestroy {
    private destroy$: Subject<void> = new Subject();
    private value = 0;
    private trickle: any = null;
    private hide: any = null;

    @ViewChild('bar') bar: ElementRef;

    get element(): HTMLElement {
        return this.bar.nativeElement;
    }

    constructor(private progress: PageProgressService, private zone: NgZone, @Inject(PLATFORM_ID) private platformId: any) { }

    ngAfterViewInit(): void {
        if (!isPlatformBrowser(this.platformId)) { return; }
        this.zone.runOutsideAngular(() => {
            this.progress.busy$.pipe(takeUntil(this.destroy$)).subscribe(busy => busy ? this.start() : this.finish());
        });
    }

    private start(): void {
        clearTimeout(this.hide);
        this.place();
        if (!this.trickle) {
            if (!this.element.classList.contains('loading-bar--on')) { this.set(0, false); }
            this.element.classList.remove('loading-bar--done');
            this.element.classList.add('loading-bar--on');
            this.element.getBoundingClientRect(); // the new start is drawn before it moves
            this.set(Math.max(this.value, 12), true);
            // Fast at first, slower as it goes, never past 94%.
            this.trickle = setInterval(() => this.set(this.value + (94 - this.value) * (this.value < 50 ? .14 : .06), true), 220);
        }
    }

    private finish(): void {
        if (!this.trickle) { return; }
        clearInterval(this.trickle);
        this.trickle = null;
        this.set(100, true);
        this.element.classList.add('loading-bar--done');
        this.hide = setTimeout(() => {
            this.element.classList.remove('loading-bar--on', 'loading-bar--done');
            this.set(0, false);
        }, 450);
    }

    private set(value: number, animate: boolean): void {
        this.value = value;
        const inner = this.element.firstElementChild as HTMLElement;
        inner.style.transition = animate ? '' : 'none';
        inner.style.transform = 'scaleX(' + (value / 100) + ')';
    }

    /// Under a sticky top bar when the page has one (the panel's), else at the top of the window.
    private place(): void {
        const top = document.querySelector('.pbar, .site-header--sticky, .header--sticky') as HTMLElement;
        const r = top ? top.getBoundingClientRect() : null;
        this.element.style.top = r && r.bottom > 0 && r.top <= 1 ? Math.round(r.bottom) + 'px' : '0px';
    }

    ngOnDestroy(): void {
        clearInterval(this.trickle);
        clearTimeout(this.hide);
        this.destroy$.next();
        this.destroy$.complete();
    }
}
